import { mergeAttributes, Node } from '@tiptap/vue-3';
import { type LiterallyAnything } from '@/types/utils';

export interface InfoPanelOptions {
	/**
	 * HTML attributes to add to the infoPanel element
	 * @default {}
	 * @example { class: 'foo' }
	 */
	HTMLAttributes: Record<string, LiterallyAnything>;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		infoPanel: {
			/**
			 * Set a InfoPanel node
			 */
			setInfoPanel: () => ReturnType;
			/**
			 * Toggle a InfoPanel node
			 */
			toggleInfoPanel: () => ReturnType;
			/**
			 * Unset a InfoPanel node
			 */
			unsetInfoPanel: () => ReturnType;
		};
	}
}

export const InfoPanel = Node.create<InfoPanelOptions>({
	name: 'infoPanel',

	addOptions() {
		return {
			HTMLAttributes: {
				class: 'text_infopanel',
			},
		};
	},

	/**
	 * Refer to docs about content and group elements
	 * https://tiptap.dev/docs/editor/core-concepts/schema#group
	 */
	content: 'block+',

	group: 'block',

	defining: true,

	parseHTML() {
		return [{ tag: 'div[class="text_infopanel"]' }];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			'div',
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
			0,
		];
	},

	addCommands() {
		return {
			setInfoPanel:
				() =>
				({ commands }) => {
					return commands.wrapIn(this.name);
				},
			toggleInfoPanel:
				() =>
				({ commands }) => {
					return commands.toggleWrap(this.name);
				},
			unsetInfoPanel:
				() =>
				({ commands }) => {
					return commands.lift(this.name);
				},
		};
	},

	addKeyboardShortcuts() {
		return {
			'Mod-Shift-1': () => this.editor.commands.toggleInfoPanel(),
		};
	},
});
